const DEFAULT_CURRENCY = "INR";

const toStringId = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  return String(value);
};

const firstDefined = (...values) => values.find((value) => value !== null && value !== undefined && value !== "");

const parseJsonValue = (value) => {
  if (!value || value === "undefined") return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getStoredSelectedCard = () => {
  if (typeof window === "undefined") return {};
  return parseJsonValue(window.localStorage?.getItem("selectedCard")) || {};
};

const getStoredSubvariantId = () => {
  if (typeof window === "undefined") return undefined;

  const storedSubId = window.localStorage?.getItem("subId");
  const parsedSubId = parseJsonValue(storedSubId);
  return firstDefined(parsedSubId, storedSubId);
};

const normalizeUrl = (url = "") => {
  if (!url) return "";
  if (typeof window === "undefined") return url;

  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
};

const getCurrentPageType = () => {
  if (typeof window === "undefined") return "";

  const pathname = window.location.pathname;
  const isRewardDetailPage = /^\/rewards\/(discount|cashback|giftcard|subscription|membership|experience)\/[^/]+\/[^/]+/.test(pathname)
    || /^\/rewards\/[^/]+\/[^/]+\/[^/]+\/[^/]+/.test(pathname);

  if (isRewardDetailPage) return "reward_detail";
  if (/^\/rewards(\/[^/]+\/[^/]+)?$/.test(pathname)) return "reward_listing";
  if (pathname === "/" || pathname === "/home") return "home";

  return pathname.split("/").filter(Boolean)[0] || "unknown";
};

export const getAnalyticsPageContext = (pageType = "") => ({
  page_type: pageType || getCurrentPageType(),
  page_path: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}${window.location.hash}` : "",
  page_location: typeof window !== "undefined" ? window.location.href : "",
  ...getAnalyticsSubvariantContext(),
});

const parseStoredUserData = () => {
  if (typeof window === "undefined") return {};

  try {
    const storedUserData = window.localStorage?.getItem("userData");
    return storedUserData ? JSON.parse(storedUserData) : {};
  } catch {
    return {};
  }
};

export const getAnalyticsUserContext = (user = {}) => {
  const storedUserData = parseStoredUserData();
  const userSource = Object.keys(user || {}).length > 0 ? user : storedUserData;
  const userId = toStringId(firstDefined(
    userSource?.id,
    userSource?.user_id,
    userSource?.uid,
    userSource?.UID,
    userSource?.user_uid
  ));

  return userId ? {
    user_id: userId,
    uid: userId,
    user: {
      id: userId,
      uid: userId,
    },
  } : {};
};

export const getAnalyticsSubvariantContext = (source = {}) => {
  const selectedCard = getStoredSelectedCard();
  const sourceSubvariant = source?.sub_variant || source?.subVariant || source?.subvariant || {};
  const storedSubvariant = selectedCard?.sub_variant || selectedCard?.subVariant || selectedCard?.subvariant || {};
  const subvariantId = toStringId(firstDefined(
    source?.subvariant_id,
    source?.sub_variant_id,
    sourceSubvariant?.id,
    storedSubvariant?.id,
    selectedCard?.subvariant_id,
    selectedCard?.sub_variant_id,
    getStoredSubvariantId()
  ));
  const subvariantName = firstDefined(
    source?.subvariant_name,
    source?.sub_variant_name,
    sourceSubvariant?.name,
    storedSubvariant?.name,
    selectedCard?.subvariant_name,
    selectedCard?.sub_variant_name
  );

  return Object.fromEntries(
    Object.entries({
      subvariant_id: subvariantId,
      subvariant_name: subvariantName,
      sub_variant_id: subvariantId,
      sub_variant_name: subvariantName,
    }).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
};

const normalizeCategory = (item = {}) => {
  if (item?.item_category) return item.item_category;
  const brandCategory = item?.brand?.brand_category || item?.brand_category || item?.brand?.category || item?.category;
  if (typeof brandCategory === "string") return brandCategory;
  return firstDefined(brandCategory?.name, item?.category_name, item?.offer_type);
};

const normalizeOfferType = (offerType = "") => {
  const offerTypeMap = {
    merchant_funded_offers: "merchant funded offer",
    gift_card: "gift card",
    affiliate_cashback: "cashback",
    subscription: "subscription",
    access_n_membership: "membership",
    experiences: "experience",
  };

  return offerTypeMap[offerType] || offerType;
};

const normalizeRegion = (item = {}) => {
  const region = item?.region || item?.country;
  if (typeof region === "string") return region;
  return firstDefined(region?.name, item?.country_name, item?.region_name);
};

const normalizeRedemptionChannel = (value) => (
  Array.isArray(value) ? value.join(", ") : value
);

const getOfferRootContext = (item = {}, analyticsItem = getAnalyticsItem(item)) => {
  const offerName = firstDefined(analyticsItem?.item_name, item?.title, item?.name, item?.brand?.name);
  const offerType = firstDefined(
    item?.root_offer_type,
    item?.analytics_offer_type,
    item?.item_category,
    analyticsItem?.item_category,
    analyticsItem?.offer_type,
    item?.offer_type
  );
  const offerCategory = firstDefined(
    item?.root_offer_category,
    item?.analytics_offer_category,
    item?.item_category,
    analyticsItem?.item_category,
    analyticsItem?.offer_type,
    item?.offer_type
  );
  const offerBrand = firstDefined(
    item?.root_offer_brand,
    item?.analytics_offer_brand,
    analyticsItem?.item_brand,
    item?.brand?.name,
    item?.brand_name,
    offerName
  );

  return Object.fromEntries(
    Object.entries({
      offer_name: offerName,
      offer_type: offerType,
      offer_category: offerCategory,
      offer_brand: offerBrand,
    }).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
};

const getElementSelector = (element) => {
  if (!element) return "";

  const tagName = element.tagName?.toLowerCase?.() || "";
  const id = element.id ? `#${element.id}` : "";
  const className = typeof element.className === "string"
    ? `.${element.className.trim().split(/\s+/).filter(Boolean).join(".")}`
    : "";

  return `${tagName}${id}${className}`;
};

const getClickUrl = ({ clickEvent, destinationUrl = "" }) => {
  const currentTarget = clickEvent?.currentTarget;
  const target = clickEvent?.target;
  const closestLink = target?.closest?.("a[href]");

  return normalizeUrl(firstDefined(
    destinationUrl,
    currentTarget?.href,
    currentTarget?.getAttribute?.("href"),
    closestLink?.href,
    closestLink?.getAttribute?.("href")
  ) || "");
};

const getClickMetadata = ({
  clickEvent, item = {}, destinationUrl = "", clickId = "", clickTarget = "", clickText = "",
} = {}) => {
  const currentTarget = clickEvent?.currentTarget;
  const target = clickEvent?.target;
  const resolvedClickId = firstDefined(
    clickId,
    currentTarget?.id,
    target?.id,
    item?.id,
    item?.offer_id,
    item?.brand_id,
    item?.brand?.id
  );

  const metadata = {
    click_id: toStringId(resolvedClickId) || "",
    click_url: getClickUrl({ clickEvent, destinationUrl }),
    click_target: firstDefined(clickTarget, currentTarget?.target, target?.target) || "",
    click_element: firstDefined(getElementSelector(currentTarget), getElementSelector(target)) || "",
    click_text: firstDefined(clickText, currentTarget?.innerText, target?.innerText, item?.title, item?.name, item?.brand?.name) || "",
  };

  return {
    ...metadata,
    "Click ID": metadata.click_id,
    "Click URL": metadata.click_url,
    "Click Target": metadata.click_target,
    "Click Element": metadata.click_element,
    "Click Text": metadata.click_text,
  };
};

export const getAnalyticsItem = (item = {}, itemListName = "") => {
  const brand = item?.brand || {};
  const normalized = {
    item_id: toStringId(firstDefined(item?.id, item?.offer_id, item?.brand_id, brand?.id)),
    item_name: firstDefined(item?.title, item?.name, brand?.name),
    item_brand: firstDefined(brand?.name, item?.brand_name, item?.name),
    item_category: normalizeCategory(item),
  };

  if (itemListName) normalized.item_list_name = itemListName;
  if (item?.offer_type) {
    normalized.offer_type = normalizeOfferType(item.offer_type);
    normalized.offer_type_key = item.offer_type;
  }
  if (item?.saving_amount !== null && item?.saving_amount !== undefined && item?.saving_amount !== "") {
    normalized.saving_amount = item.saving_amount;
  }
  if (item?.points_to_claim !== null && item?.points_to_claim !== undefined && item?.points_to_claim !== "") {
    normalized.points_to_claim = item.points_to_claim;
  }
  if (item?.expiry) normalized.expiry_date = item.expiry;
  if (item?.amount_to_claim !== null && item?.amount_to_claim !== undefined && item?.amount_to_claim !== "") {
    normalized.amount_to_claim = item.amount_to_claim;
  }
  if (item?.offer_type_detail) normalized.offer_type_detail = normalizeRedemptionChannel(item.offer_type_detail);
  if (item?.redemption_channel) normalized.redemption_channel = normalizeRedemptionChannel(item.redemption_channel);
  if (normalizeRegion(item)) normalized.region = normalizeRegion(item);
  if (item?.slug) normalized.slug = item.slug;

  Object.assign(normalized, getAnalyticsSubvariantContext(item));

  return Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
};

export const getAnalyticsItems = (items = [], itemListName = "") => (
  Array.isArray(items) 
  ? items.map((item) => getAnalyticsItem(item, itemListName)).filter((item) => item.item_id || item.item_name) 
  : []
);

const pushToDataLayer = (payload = {}) => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...getAnalyticsUserContext(),
    ...getAnalyticsSubvariantContext(payload?.item || payload),
    ...payload,
  });
};

const pushEcommerceEvent = (payload = {}) => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    ...getAnalyticsUserContext(),
    ...getAnalyticsSubvariantContext(payload?.item || payload),
    ...payload,
  });
};

export const trackItemListView = ({ customEventName, itemListName, items = [], pageType = "" }) => {
  const analyticsItems = getAnalyticsItems(items, itemListName);
  if (analyticsItems.length === 0) return;

  pushEcommerceEvent({
    event: "view_item_list",
    custom_event_name: customEventName || "view_item_list",
    ...getAnalyticsPageContext(pageType),
    item_list_name: itemListName,
    ecommerce: {
      items: analyticsItems,
    },
  });
};

export const trackItemSelect = ({
  eventName = "select_item", customEventName, itemListName, item = {}, clickEvent, destinationUrl = "", clickId = "",
  clickTarget = "", clickText = "", pageType = ""
}) => {
  const analyticsItem = getAnalyticsItem(item, itemListName);
  if (!analyticsItem.item_id && !analyticsItem.item_name) return;

  pushEcommerceEvent({
    event: eventName,
    custom_event_name: customEventName || eventName,
    ga4_event_name: "select_item",
    ...getAnalyticsPageContext(pageType),
    ...getOfferRootContext(item, analyticsItem),
    ...analyticsItem,
    item_list_name: itemListName,
    ...getClickMetadata({ clickEvent, item, destinationUrl, clickId, clickTarget, clickText }),
    ecommerce: {
      items: [analyticsItem],
    },
  });
};

export const trackItemView = ({
  eventName = "view_item", customEventName, item = {}, currency = DEFAULT_CURRENCY, clickId = "", cardId = "", pageType = "",
}) => {
  const analyticsItem = getAnalyticsItem(item);
  if (!analyticsItem.item_id && !analyticsItem.item_name) return;

  pushEcommerceEvent({
    event: eventName,
    custom_event_name: customEventName || eventName,
    ga4_event_name: "view_item",
    ...getAnalyticsPageContext(pageType),
    ...analyticsItem,
    ...getClickMetadata({ item, clickId }),
    card_id: toStringId(cardId) || "",
    currency,
    ecommerce: {
      items: [analyticsItem],
    },
  });
};

export const trackFilterApplied = ({ 
  filterType, filterValue, clickEvent, destinationUrl = "", clickId = "", clickTarget = "", pageType = "" 
}) => {
  pushToDataLayer({
    event: "filter_applied",
    custom_event_name: "reward_filter_applied",
    ecommerce: null,
    ...getAnalyticsPageContext(pageType),
    filter_type: filterType,
    filter_value: filterValue,
    ...getClickMetadata({
      clickEvent,
      destinationUrl,
      clickId,
      clickTarget,
      item: {
        id: filterValue,
        title: filterValue,
      },
    }),
  });
};

export const getRedemptionType = (item = {}) => {
  const offerTypeDetail = item?.offer_type_detail;
  if (offerTypeDetail === "STATIC_CODE" || offerTypeDetail?.includes?.("STATIC_CODE")) return "Static Code";
  if (offerTypeDetail === "DYNAMIC_CODE" || offerTypeDetail?.includes?.("DYNAMIC_CODE")) return "Dynamic Code";
  if (offerTypeDetail === "REDIRECTION_URL" || offerTypeDetail?.includes?.("REDIRECTION_URL")) return "Direct Link";
  if (item?.offer_type === "affiliate_cashback") return "Direct Link";
  return offerTypeDetail || "Unknown";
};

export const trackCouponGenerated = ({
  eventName = "generate_coupon", customEventName, item = {}, couponCode = "", destinationUrl = "", pageType = "",
}) => {
  const analyticsItem = getAnalyticsItem(item);

  pushEcommerceEvent({
    event: eventName,
    custom_event_name: customEventName || eventName,
    ...getAnalyticsPageContext(pageType),
    ...analyticsItem,
    coupon_code: couponCode,
    redemption_type: getRedemptionType(item),
    destination_url: destinationUrl,
    ecommerce: {
      items: [analyticsItem],
    },
  });
};

export const trackCouponCopied = ({ 
  eventName = "copy_coupon", customEventName, item = {}, couponCode = "", clickEvent, destinationUrl = "", clickId = "",
  clickTarget = "", clickText = "", pageType = ""
}) => {
  const analyticsItem = getAnalyticsItem(item);

  pushEcommerceEvent({
    event: eventName,
    custom_event_name: customEventName || eventName,
    ...getAnalyticsPageContext(pageType),
    ...analyticsItem,
    coupon_code: couponCode,
    redemption_type: getRedemptionType(item),
    ...getClickMetadata({ clickEvent, item, destinationUrl, clickId, clickTarget, clickText }),
    ecommerce: {
      items: [analyticsItem],
    },
  });
};

export const trackMerchantLinkClick = ({ 
  eventName = "click_merchant_link", customEventName, item = {}, destinationUrl = "", clickEvent, clickId = "",
  clickTarget = "", clickText = "", pageType = ""
}) => {
  const analyticsItem = getAnalyticsItem(item);

  pushEcommerceEvent({
    event: eventName,
    custom_event_name: customEventName || eventName,
    ...getAnalyticsPageContext(pageType),
    ...analyticsItem,
    ...getClickMetadata({ clickEvent, item, destinationUrl, clickId, clickTarget, clickText }),
    destination_url: destinationUrl,
    redemption_type: getRedemptionType(item),
    ecommerce: {
      items: [analyticsItem],
    },
  });
};

export const trackRewardDetailCtaClick = ({
  eventName = "reward detail click to redeem", customEventName, item = {}, ctaName = "", clickEvent, destinationUrl = "",
  clickId = "", clickTarget = "", clickText = "", couponCode = "", redemptionType = "", orderId = "", cardId = "",
  value = undefined, currency = DEFAULT_CURRENCY, pageType = "",
} = {}) => {
  const analyticsItem = getAnalyticsItem(item);

  pushEcommerceEvent({
    event: eventName,
    custom_event_name: customEventName || eventName,
    ...getAnalyticsPageContext(pageType),
    cta_name: ctaName,
    item_id: analyticsItem.item_id,
    item_name: analyticsItem.item_name,
    item_brand: analyticsItem.item_brand,
    item_category: analyticsItem.item_category,
    offer_type: analyticsItem.offer_type,
    offer_type_key: analyticsItem.offer_type_key,
    saving_amount: analyticsItem.saving_amount,
    points_to_claim: analyticsItem.points_to_claim,
    expiry_date: analyticsItem.expiry_date,
    amount_to_claim: analyticsItem.amount_to_claim,
    offer_type_detail: analyticsItem.offer_type_detail,
    redemption_channel: analyticsItem.redemption_channel,
    region: analyticsItem.region,
    slug: analyticsItem.slug,
    coupon_code: couponCode,
    redemption_type: redemptionType || getRedemptionType(item),
    order_id: orderId,
    card_id: toStringId(cardId) || "",
    value,
    currency,
    ...getClickMetadata({ clickEvent, item, destinationUrl, clickId, clickTarget, clickText }),
    ecommerce: {
      items: [analyticsItem],
    },
  });
};

export const trackAuthSuccess = ({
  action = "sign_in", method = "otp", message = "", user = {}, pageType = "",
} = {}) => {
  const isSignUp = action === "sign_up";
  const userContext = getAnalyticsUserContext(user);

  pushToDataLayer({
    event: isSignUp ? "sign_up" : "login",
    custom_event_name: isSignUp ? "user_signed_up" : "user_signed_in",
    ecommerce: null,
    ...getAnalyticsPageContext(pageType),
    auth_action: action,
    auth_method: method,
    auth_status: "success",
    message,
    ...userContext,
  });
};

export const trackSearch = ({
  searchTerm = "", searchSource = "global_search", resultType = "", resultId = "", resultName = "", destinationUrl = "",
  pageType = "",
} = {}) => {
  const normalizedSearchTerm = String(searchTerm || "").trim();
  if (!normalizedSearchTerm) return;

  pushToDataLayer({
    event: "search",
    custom_event_name: searchSource,
    ecommerce: null,
    ...getAnalyticsPageContext(pageType),
    search_term: normalizedSearchTerm,
    search_keyword: normalizedSearchTerm,
    search_source: searchSource,
    result_type: resultType,
    result_id: toStringId(resultId) || "",
    result_name: resultName,
    destination_url: normalizeUrl(destinationUrl),
  });
};

export const getItemListSignature = (items = [], itemListName = "") => {
  const ids = getAnalyticsItems(items, itemListName).map((item) => item.item_id || item.item_name);
  return `${itemListName}:${ids.join("|")}`;
};
